// ActiveSymbolsService: core extraction of symbol processing & categorization
// NOTE: This is a framework-agnostic service (no MobX). It processes raw active symbols
// and provides categorized structures plus market status updates.

export type RawActiveSymbol = {
    symbol: string;
    display_name: string;
    market: string;
    market_display_name: string;
    subgroup: string;
    subgroup_display_name: string;
    submarket_display_name: string;
    exchange_is_open?: number | boolean;
    pip: number; // pip value used to derive decimal places
};

export type RawActiveSymbols = RawActiveSymbol[];

export type TProcessedSymbolItem = {
    symbol: string;
    name: string;
    market: string;
    market_display_name: string;
    subgroup: string;
    subgroup_display_name: string;
    submarket_display_name: string;
    exchange_is_open: boolean;
    decimal_places: number;
};

export type TProcessedSymbols = TProcessedSymbolItem[];

export type TSubCategoryDataItem = {
    enabled: boolean;
    itemId: string;
    display: string;
    dataObject: TProcessedSymbolItem;
    selected?: boolean;
};

export type TSubCategoryData = TSubCategoryDataItem[];

export type TSubCategory = {
    subcategoryName: string;
    data: TSubCategoryDataItem[];
};

export type TCategorizedSymbolItem<T = TSubCategory> = {
    categoryName: string;
    categoryId: string;
    hasSubcategory: boolean;
    hasSubgroup: boolean;
    subgroups: TCategorizedSymbolItem[];
    data: T[];
    active?: boolean;
};

export type TCategorizedSymbols = TCategorizedSymbolItem[];

export type TMarketStatusChanges = Record<string, boolean>;

export interface IActiveSymbolsService {
    load(raw: RawActiveSymbols, force?: boolean): Promise<void>;
    getProcessedSymbols(): TProcessedSymbolItem[];
    getCategorizedSymbols(): TCategorizedSymbols;
    getSymbolMap(): Record<string, TProcessedSymbolItem>;
    getSymbol(symbol: string): TProcessedSymbolItem | undefined;
    onMarketStatusChange(cb: (changes: TMarketStatusChanges) => void): () => void;
    applyMarketStatusChanges(changes: TMarketStatusChanges): void;
}

const DEFAULT_MARKET_ORDER = [
    'synthetic_index',
    'basket_index',
    'forex',
    'indices',
    'stocks',
    'cryptocurrency',
    'commodities',
];

export class ActiveSymbolsService implements IActiveSymbolsService {
    private _processed: TProcessedSymbols = [];
    private _categorized: TCategorizedSymbols = [];
    private _symbolMap: Record<string, TProcessedSymbolItem> = {};
    private _listeners: Set<(changes: TMarketStatusChanges) => void> = new Set();
    private _loaded = false;
    private _marketOrder: string[] = DEFAULT_MARKET_ORDER;

    async load(raw: RawActiveSymbols, force = false, marketOrder?: string[]): Promise<void> {
        if (this._loaded && !force) return;
        if (marketOrder && marketOrder.length) {
            this._marketOrder = marketOrder.slice();
        }
        this._processed = this._processSymbols(raw);
        this._categorized = this._categorizeActiveSymbols(this._processed);
        this._symbolMap = {};
        for (const s of this._processed) {
            this._symbolMap[s.symbol] = s;
        }
        this._loaded = true;
    }

    getProcessedSymbols(): TProcessedSymbolItem[] {
        return this._processed.slice();
    }

    getCategorizedSymbols(): TCategorizedSymbols {
        // Return deep clone of categorized symbols to avoid external mutation of internal state
        return this._categorized.map(cat => ({
            ...cat,
            data: cat.data.map(sub => ({
                ...sub,
                data: sub.data.map(item => ({ ...item, dataObject: { ...item.dataObject } })),
            })),
            subgroups: cat.subgroups.map(subgroup => ({
                ...subgroup,
                data: subgroup.data.map(sub => ({
                    ...sub,
                    data: sub.data.map(item => ({ ...item, dataObject: { ...item.dataObject } })),
                })),
            })),
        }));
    }

    getSymbolMap(): Record<string, TProcessedSymbolItem> {
        return { ...this._symbolMap };
    }

    getSymbol(symbol: string): TProcessedSymbolItem | undefined {
        return this._symbolMap[symbol];
    }

    onMarketStatusChange(cb: (changes: TMarketStatusChanges) => void): () => void {
        this._listeners.add(cb);
        return () => this._listeners.delete(cb);
    }

    applyMarketStatusChanges(changes: TMarketStatusChanges): void {
        for (const sym in changes) {
            const obj = this._symbolMap[sym];
            if (obj) obj.exchange_is_open = changes[sym];
        }
        for (const l of this._listeners) l(changes);
    }

    private _processSymbols(raw: RawActiveSymbols): TProcessedSymbols {
        // Stable sort by submarket_display_name maintaining original order on ties
        const sorted = raw
            .map((s, idx) => ({ s, idx }))
            .sort((a, b) => {
                const cmp = a.s.submarket_display_name.localeCompare(b.s.submarket_display_name);
                return cmp !== 0 ? cmp : a.idx - b.idx;
            })
            .map(w => w.s);

        const processed: TProcessedSymbols = sorted.map(s => ({
            symbol: s.symbol,
            name: s.display_name,
            market: s.market,
            market_display_name: s.market_display_name,
            subgroup: s.subgroup,
            subgroup_display_name: s.subgroup_display_name,
            submarket_display_name: s.submarket_display_name,
            exchange_is_open: !!s.exchange_is_open,
            decimal_places: s.pip.toString().length - 2,
        }));

        // Reorder by predefined market order for UX expectations
        const ordered: TProcessedSymbols = [];
        for (const market of this._marketOrder) {
            for (const p of processed) {
                if (p.market === market) ordered.push(p);
            }
        }
        // Append any markets not in DEFAULT_MARKET_ORDER preserving current order
        for (const p of processed) {
            if (!this._marketOrder.includes(p.market)) ordered.push(p);
        }
        return ordered;
    }

    private _categorizeActiveSymbols(activeSymbols: TProcessedSymbols): TCategorizedSymbols {
        if (activeSymbols.length === 0) return [];
        const categorized: TCategorizedSymbols = [];
        const makeSubcategory = (d: TProcessedSymbolItem): TSubCategory => ({
            subcategoryName: d.submarket_display_name,
            data: [],
        });
        const makeCategory = (d: TProcessedSymbolItem): TCategorizedSymbolItem => ({
            categoryName: d.market_display_name,
            categoryId: d.market,
            hasSubcategory: true,
            hasSubgroup: !!(d.subgroup && d.subgroup !== 'none'),
            data: [],
            subgroups: [],
        });

        let category = makeCategory(activeSymbols[0]);
        let subcategory = makeSubcategory(activeSymbols[0]);

        for (const sym of activeSymbols) {
            if (category.categoryName !== sym.market_display_name && category.categoryName !== sym.subgroup_display_name) {
                category.data.push(subcategory);
                categorized.push(category);
                category = makeCategory(sym);
                subcategory = makeSubcategory(sym);
            }

            if (category.hasSubgroup) {
                let subgroup = category.subgroups.find(sg => sg.categoryId === sym.subgroup);
                if (!subgroup) {
                    subgroup = {
                        data: [],
                        categoryName: sym.subgroup_display_name,
                        categoryId: sym.subgroup,
                        hasSubcategory: true,
                        hasSubgroup: false,
                        subgroups: [],
                    };
                    category.subgroups.push(subgroup);
                }
                let subgroupSubcat = subgroup.data.find(sc => sc.subcategoryName === sym.submarket_display_name);
                if (!subgroupSubcat) {
                    subgroupSubcat = makeSubcategory(sym);
                    subgroup.data.push(subgroupSubcat);
                }
                subgroupSubcat.data.push({
                    enabled: true,
                    itemId: sym.symbol,
                    display: sym.name,
                    dataObject: sym,
                });
            }

            if (subcategory.subcategoryName !== sym.submarket_display_name) {
                category.data.push(subcategory);
                subcategory = makeSubcategory(sym);
            }
            subcategory.data.push({
                enabled: true,
                itemId: sym.symbol,
                display: sym.name,
                dataObject: sym,
            });
        }
        category.data.push(subcategory);
        categorized.push(category);
        return categorized;
    }
}

export default ActiveSymbolsService;
